import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MenuItem, Select, Stack, Typography, useTheme } from '@mui/material';
import { useSelector } from 'react-redux';
import { useYear } from '../../../../core/year/hook/useYear';
import year from '../../../../module/year';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

const Season = () => {
  const theme = useTheme();
  const initializedRef = useRef(false);

  const { from, to } = useSelector((state) => state.year);
  const { handleChangeYear } = useYear(year.filter.actions.changeYear);

  const [startYear, setStartYear] = useState(null);
  const [endYear, setEndYear] = useState(null);
  const [availableYears, setAvailableYears] = useState([]);

  const generateYears = useCallback(() => {
    const fromYear = 2022;
    const currentYear = dayjs.utc().year();
    const years = [];

    for (let y = fromYear; y <= currentYear; y++) {
      years.push(y);
    }

    return years;
  }, []);

  // Initialize available years on mount
  useEffect(() => {
    const years = generateYears();
    setAvailableYears(years);
  }, [generateYears]);

  useEffect(() => {
    const currentYear = dayjs.utc().year();
    if (from && to) {
      setStartYear(dayjs.utc(from).year());
      setEndYear(dayjs.utc(to).year());
      initializedRef.current = true;
      return;
    }

    if (!initializedRef.current) {
      setStartYear(currentYear);
      setEndYear(currentYear);
      handleChangeYear({
        from: dayjs.utc(`${currentYear}-01-01T00:00:00`).toISOString(),
        to: dayjs.utc(`${currentYear}-12-31T00:00:00`).endOf('day').toISOString(),
      });
      initializedRef.current = true;
    }
  }, [from, to, handleChangeYear]);

  const handleStartYearChange = (event) => {
    const newStartYear = Number(event.target.value);
    const finalEndYear = endYear && endYear >= newStartYear ? endYear : newStartYear;

    setStartYear(newStartYear);
    setEndYear(finalEndYear);
    handleChangeYear({
      from: dayjs.utc(`${newStartYear}-01-01T00:00:00`).toISOString(),
      to: dayjs.utc(`${finalEndYear}-12-31T00:00:00`).endOf('day').toISOString(),
    });
  };

  const handleEndYearChange = (event) => {
    const newEndYear = Number(event.target.value);
    const finalStartYear = startYear && startYear <= newEndYear ? startYear : newEndYear;

    setEndYear(newEndYear);
    setStartYear(finalStartYear);
    handleChangeYear({
      from: dayjs.utc(`${finalStartYear}-01-01T00:00:00`).toISOString(),
      to: dayjs.utc(`${newEndYear}-12-31T00:00:00`).endOf('day').toISOString(),
    });
  };

  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{
        alignItems: 'center',
        px: 1,
      }}
    >
      <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
        From:
      </Typography>
      <Select
        value={startYear || ''}
        onChange={handleStartYearChange}
        size="small"
        sx={{
          minWidth: '80px',
          backgroundColor: theme.palette.primary.light,
          color: 'black',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
          },
        }}
      >
        {availableYears.map((year) => (
          <MenuItem key={year} value={year}>
            {year}
          </MenuItem>
        ))}
      </Select>

      <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
        To:
      </Typography>
      <Select
        value={endYear || ''}
        onChange={handleEndYearChange}
        size="small"
        sx={{
          minWidth: '80px',
          backgroundColor: theme.palette.primary.light,
          color: 'black',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
          },
        }}
      >
        {availableYears.map((year) => (
          <MenuItem key={year} value={year}>
            {year}
          </MenuItem>
        ))}
      </Select>
    </Stack>
  );
};

export default Season;
